/**
 * Einfacher, robuster Hash-Router (#/pfad/:param?query=...).
 * Hash-Routing funktioniert ohne Server-Konfiguration sowohl im Browser
 * als auch in Capacitor (file://-Origin).
 */

export interface RouteContext {
  path: string;
  params: Record<string, string>;
  query: URLSearchParams;
}

export type View = HTMLElement | Promise<HTMLElement>;
export type RouteHandler = (ctx: RouteContext) => View;

export interface RouteMeta {
  requiresAuth?: boolean;
  layout?: "app" | "blank";
  [key: string]: unknown;
}

interface RouteDef {
  pattern: string;
  handler: RouteHandler;
  meta: RouteMeta;
}

type BeforeEach = (
  ctx: RouteContext,
  meta: RouteMeta
) => string | void | Promise<string | void>;

type RenderFn = (view: HTMLElement, ctx: RouteContext, meta: RouteMeta) => void;

function matchPattern(
  pattern: string,
  path: string
): Record<string, string> | null {
  const pp = pattern.replace(/^\//, "").split("/").filter(Boolean);
  const ps = path.replace(/^\//, "").split("/").filter(Boolean);
  if (pp.length !== ps.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < pp.length; i++) {
    const seg = pp[i];
    if (seg.startsWith(":")) {
      params[seg.slice(1)] = decodeURIComponent(ps[i]);
    } else if (seg !== ps[i]) {
      return null;
    }
  }
  return params;
}

export class Router {
  private routes: RouteDef[] = [];
  private notFoundHandler?: RouteHandler;
  private beforeEachFn?: BeforeEach;
  private renderFn?: RenderFn;

  add(pattern: string, handler: RouteHandler, meta: RouteMeta = {}): this {
    this.routes.push({ pattern, handler, meta });
    return this;
  }

  setNotFound(handler: RouteHandler): this {
    this.notFoundHandler = handler;
    return this;
  }

  beforeEach(fn: BeforeEach): this {
    this.beforeEachFn = fn;
    return this;
  }

  onRender(fn: RenderFn): this {
    this.renderFn = fn;
    return this;
  }

  /**
   * `resolve()` darf nie stillschweigend scheitern: passiert das, bleibt die
   * alte Seite stehen und ein Klick sieht aus, als hätte er nichts getan.
   */
  private run(): void {
    this.resolve().catch((err) => {
      console.error("[router] Navigation ist gescheitert:", this.currentFull(), err);
    });
  }

  start(): void {
    window.addEventListener("hashchange", () => this.run());
    this.run();
  }

  navigate(path: string): void {
    const target = path.startsWith("#") ? path.slice(1) : path;
    // Nicht nur den Pfad vergleichen, sondern den ganzen Hash (inklusive
    // Query): sonst ging beim bloßen Ändern der Query (z. B. ein Filter auf
    // derselben Seite) das `hashchange`-Ereignis nie los, weil sich für den
    // Vergleich unten nichts geändert hatte – der neue Filter landete zwar in
    // der Adresse, wurde aber nie ausgewertet. Ändert sich der Hash-String
    // tatsächlich nicht (identischer Aufruf derselben Seite ohne Query-
    // Änderung), feuert der Browser kein Ereignis – dann von Hand auflösen.
    const before = location.hash.replace(/^#/, "");
    location.hash = target;
    if (location.hash.replace(/^#/, "") === before) this.run();
  }

  private currentFull(): string {
    const raw = location.hash.replace(/^#/, "");
    return raw || "/";
  }

  private async resolve(): Promise<void> {
    const full = this.currentFull();
    const [pathRaw, queryStr = ""] = full.split("?");
    const path = pathRaw || "/";
    const query = new URLSearchParams(queryStr);

    let matched: { def: RouteDef; params: Record<string, string> } | null = null;
    for (const def of this.routes) {
      const params = matchPattern(def.pattern, path);
      if (params) {
        matched = { def, params };
        break;
      }
    }

    const ctx: RouteContext = {
      path,
      params: matched?.params ?? {},
      query,
    };
    const meta: RouteMeta = matched?.def.meta ?? {};

    if (this.beforeEachFn) {
      try {
        const redirect = await this.beforeEachFn(ctx, meta);
        if (redirect && redirect !== path) {
          location.hash = redirect;
          return;
        }
      } catch (err) {
        console.error("[router] beforeEach ist gescheitert:", path, err);
      }
    }

    let view: HTMLElement;
    try {
      if (matched) view = await matched.def.handler(ctx);
      else if (this.notFoundHandler) view = await this.notFoundHandler(ctx);
      else {
        const el = document.createElement("div");
        el.textContent = "404";
        view = el;
      }
    } catch (err) {
      console.error("[router] Seite konnte nicht geladen werden:", path, err);
      const el = document.createElement("div");
      el.className = "page-error";
      el.textContent =
        "Fehler beim Laden der Seite: " +
        (err instanceof Error ? err.message : String(err));
      view = el;
    }

    this.renderFn?.(view, ctx, meta);
  }
}

/** Singleton-Router der App. Konfiguration erfolgt in main.ts. */
export const router = new Router();

/** Bequemes Navigieren aus Komponenten heraus. */
export function navigate(path: string): void {
  router.navigate(path);
}
